import { ListFilter } from "lucide-react"
import { ReactNode, useState } from "react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

export type SidebarFacetSelection = {
  value: string
  label: string
  control?: ReactNode
}

/**
 * Presentation-only shell for Redux and non-Redux facet adapters. Applied
 * selections remain indicated on the trigger and visible while the popover is open.
 */
export function SidebarFacetPopover({
  label,
  selections,
  onToggle,
  filter,
  hasAvailableOptions = true,
  showSelectedPills = false,
  children,
}: {
  label: string
  selections: SidebarFacetSelection[]
  onToggle: (value: string) => void
  filter?: ReactNode
  hasAvailableOptions?: boolean
  showSelectedPills?: boolean
  children: ReactNode
}) {
  const [open, setOpen] = useState(false)
  const { t } = useTranslation(["common", "search"])
  const filterByLabel = t("search:facet.filter_by", { facet: label })
  const triggerLabel =
    selections.length > 0
      ? t("search:facet.selected_count", {
          facet: label,
          count: selections.length,
        })
      : filterByLabel

  return (
    <fieldset className="flex max-w-full min-w-0 flex-col gap-2 pb-3">
      <legend className="sr-only">{label}</legend>
      <Popover open={open} onOpenChange={setOpen} modal>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="h-auto max-w-full min-w-0 justify-start rounded-full text-left break-words whitespace-normal"
          >
            <ListFilter aria-hidden />
            <span className="min-w-0 break-words">{triggerLabel}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          side="bottom"
          avoidCollisions={false}
          className="flex max-h-[min(24rem,calc(100vh-2rem))] w-[min(24rem,calc(100vw-2rem))] flex-col overflow-y-auto overscroll-contain"
        >
          <div className="mb-3 flex min-w-0 shrink-0 items-start justify-between gap-2">
            <h2 className="min-w-0 font-medium break-words">{label}</h2>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="shrink-0"
              onClick={() => setOpen(false)}
            >
              {t("actions.done")}
            </Button>
          </div>
          <div className="flex flex-col">
            {filter && <div className="shrink-0">{filter}</div>}
            {selections.length > 0 && (
              <fieldset
                className={`${filter ? "mt-3" : ""} flex min-w-0 flex-col gap-1.5`}
              >
                <legend className="font-medium">
                  {t("search:facet.selected")}
                </legend>
                {selections.map(
                  (selection) =>
                    selection.control ?? (
                      <label
                        className="flex min-w-0 cursor-pointer items-start gap-2 text-sm"
                        key={selection.value}
                      >
                        <Checkbox
                          className="mt-0.5 shrink-0"
                          aria-label={selection.label}
                          checked
                          onCheckedChange={() => onToggle(selection.value)}
                        />
                        <span className="min-w-0 flex-1 break-words">
                          {selection.label}
                        </span>
                      </label>
                    ),
                )}
              </fieldset>
            )}
            {hasAvailableOptions && (
              <fieldset
                className={`flex min-w-0 flex-col ${filter || selections.length > 0 ? "mt-3" : ""}`}
              >
                <legend className="shrink-0 font-medium">
                  {t("search:facet.available_options")}
                </legend>
                <div>{children}</div>
              </fieldset>
            )}
          </div>
        </PopoverContent>
      </Popover>
      {showSelectedPills && selections.length > 0 && (
        <div
          className="flex max-w-full min-w-0 flex-wrap gap-1"
          aria-label={t("search:facet.selected")}
        >
          {selections.map((selection) => (
            <Badge
              asChild
              className="max-w-full min-w-0 gap-1 pr-1"
              key={selection.value}
              variant="active"
            >
              <button
                type="button"
                aria-label={`Remove ${selection.label}`}
                className="flex max-w-full min-w-0 items-center"
                onClick={() => onToggle(selection.value)}
              >
                <span className="min-w-0 truncate">{selection.label}</span>
              </button>
            </Badge>
          ))}
        </div>
      )}
    </fieldset>
  )
}
